import { ShortCode } from '@highereducation/wordpress';
import { Flex } from '@highereducation/hen-ui';

type FlexDirection = 'column' | 'row';
type FlexGap =
  | 'none'
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | 'max';
type FlexWrap = 'wrap' | 'nowrap';
type FlexAlign = 'start' | 'center' | 'end' | 'stretch';
type FlexJustify = 'start' | 'center' | 'end' | 'between';
type FlexHeight = 'auto' | 'full';
type FlexWidth = 'full' | 'auto' | 'article';

interface FlexShortcodeProps {
  direction?: FlexDirection;
  gap?: FlexGap;
  wrap?: FlexWrap;
  align?: FlexAlign;
  'align-md'?: FlexAlign;
  justify?: FlexJustify;
  height?: FlexHeight;
  width?: FlexWidth;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * FlexShortcode - Shortcode wrapper for Flex layout component
 *
 * Use for vertical or horizontal arrangements with consistent spacing.
 * direction="column" with gap is ideal for article sections.
 *
 * Supported shortcode attributes:
 * - direction: 'column' | 'row' (default: 'column')
 * - gap: 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'max' (default: 'md')
 * - wrap: 'wrap' | 'nowrap'
 * - align: 'start' | 'center' | 'end' | 'stretch'
 * - justify: 'start' | 'center' | 'end' | 'between'
 * - height: 'auto' | 'full'
 * - width: 'auto' | 'full' | 'article' — article centers and caps at article content (`mx-auto max-w-article-content w-full`). Default: auto.
 *
 * @example WordPress shortcode usage:
 * [hen-ui-flex direction="column" gap="lg" height="auto"]
 *   [hen-ui-heading variant="h3"]Section Title[/hen-ui-heading]
 *   [hen-ui-text variant="b4"]Content here.[/hen-ui-text]
 * [/hen-ui-flex]
 *
 * @example Article-width column (editorial sections):
 * [hen-ui-flex direction="column" gap="xl" width="article"]
 *   ...
 * [/hen-ui-flex]
 */
export const FlexShortcode: ShortCode<FlexShortcodeProps> = {
  balanced: true,
  component: ({
    direction = 'column',
    gap = 'md',
    wrap = 'nowrap',
    align = 'stretch',
    'align-md': alignMd,
    justify = 'start',
    height = 'auto',
    width = 'auto',
    children,
  }) => (
    <Flex
      direction={direction}
      gap={gap}
      wrap={wrap}
      align={align}
      align-md={alignMd}
      justify={justify}
      height={height}
      width={width}
    >
      {children}
    </Flex>
  ),
};
